//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { Button, ButtonTypes } from 'common/components/core/button';
import map from 'lodash/map';
import findIndex from 'lodash/findIndex';
import compose from 'recompose/compose';
import withHandlers from 'recompose/withHandlers';
import theme from './ButtonGroup.scss';

type SourceType<TValue> = {
    label: string,
    value: TValue
};

type Props<TValue> = {
    values: Array<SourceType<TValue>>,
    onChange: (Array<SourceType<TValue>>) => void,
    isMulti?: boolean,
    // TODO: Fix ButtonGroup api. Why defaultSelected required
    defaultSelected?: TValue,
    selectedValues: Array<TValue>,
    theme: Object,
    invert?: boolean
};

type State<TValue> = {
    selectedButtons: Array<SourceType<TValue>>
};

const enhancer = compose(
    withHandlers({
        handleButtonClick: ({ onButtonClick, value }) => () =>
            onButtonClick(value)
    })
);

const EnhancedButton = enhancer(({ handleButtonClick, ...others }) => {
    return <Button onClick={handleButtonClick} {...others} />;
});

class ButtonGroup<TValue> extends React.Component<
    Props<TValue>,
    State<TValue>
> {
    state: State<TValue>;
    constructor(props: Props<TValue>) {
        super(props);

        let defaultIndex = -1;
        if (props.defaultSelected) {
            defaultIndex = this.getItemIndex(
                props.defaultSelected,
                props.values
            );
        }

        this.state = {
            selectedButtons:
                defaultIndex !== -1 ? [props.values[defaultIndex]] : []
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props<TValue>) {
        const { selectedValues, values } = nextProps;
        if (selectedValues) {
            const selectedIndexesArray = selectedValues
                .map(value => this.getItemIndex(value, values))
                .filter(index => index !== -1);
            this.setState({
                selectedButtons: selectedIndexesArray.map(
                    index => values[index]
                )
            });
        }
    }

    getItemIndex(value: TValue, inArray: Array<SourceType<TValue>>): number {
        return findIndex(inArray, item => {
            return item.value === value;
        });
    }

    handleSingleSelect = (value: string) => {
        const { values, onChange } = this.props;
        const globalIndex = this.getItemIndex(value, values);
        const selectedButtons = [values[globalIndex]];
        onChange(selectedButtons);
        this.setState({
            selectedButtons
        });
    };

    handleMultiSelect = (value: string) => {
        const { values, onChange } = this.props;
        const { selectedButtons } = this.state;
        const globalIndex = this.getItemIndex(value, values);
        const buttonIndex = this.getItemIndex(value, selectedButtons);
        let selectedButtonsArray: Array<SourceType> = [];
        if (buttonIndex === -1) {
            selectedButtonsArray = [
                ...selectedButtons.slice(0, buttonIndex),
                values[globalIndex],
                ...selectedButtons.slice(buttonIndex)
            ];
            onChange(selectedButtons);
            this.setState({
                selectedButtons
            });
        } else {
            selectedButtonsArray = [
                ...selectedButtons.slice(0, buttonIndex),
                ...selectedButtons.slice(buttonIndex + 1)
            ];
            this.setState({
                selectedButtonsArray
            });
            onChange(selectedButtonsArray);
        }
    };

    buttonClick = (value: string) => {
        const { isMulti } = this.props;

        isMulti
            ? this.handleMultiSelect(value)
            : this.handleSingleSelect(value);
    };

    isActive = (value: string) => {
        const { selectedButtons } = this.state;
        const buttonIndex = this.getItemIndex(value, selectedButtons);
        return buttonIndex !== -1;
    };

    renderButtons = () => {
        const { values, theme, invert } = this.props;
        return map(values, buttonValue => {
            const { value, label, testId } = buttonValue;
            return (
                <EnhancedButton
                    testId={testId}
                    key={value}
                    inverted={invert}
                    className={classnames({
                        [theme.isActive]: this.isActive(value)
                    })}
                    theme={theme}
                    uppercase={false}
                    raised={false}
                    ripple={false}
                    buttonType={ButtonTypes.Solid}
                    onButtonClick={this.buttonClick}
                    value={value}
                    label={label}
                />
            );
        });
    };

    render() {
        const { theme, children } = this.props;

        return (
            <div className={theme.buttonGroupWrapper}>
                {this.renderButtons()}
                {children}
            </div>
        );
    }
}

export const ThemedButtonGroup = themr('ButtonGroup', theme)(ButtonGroup);
export default ThemedButtonGroup;
